/**
 * RMSUnlockModal
 *
 * 2FA unlock flow modal with:
 * - Reflection text input
 * - OTP request and verification
 * - Countdown timer for OTP validity
 * - Emergency unlock option
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Lock,
  Clock,
  Mail,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { useDisciplineStore, LockMode } from '../../store/disciplineStore';
import { useToastStore } from '../../store/toastStore';
import './RMSUnlockModal.css';

const LOCK_MODE_LABELS: Record<LockMode, string> = {
  unlocked: 'Unlocked',
  cool_off: 'Cool Off',
  done_for_day: 'Done for Day',
  revenge_guard: 'Revenge Guard',
  tilt_protection: 'Tilt Protection',
};

const formatTime = (ms: number): string => {
  if (ms <= 0) return '0:00';
  const seconds = Math.floor(ms / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

type Step = 'reflection' | 'otp' | 'success';

const RMSUnlockModal: React.FC = () => {
  const isOpen = useDisciplineStore((s) => s.unlockModalOpen);
  const closeModal = useDisciplineStore((s) => s.closeUnlockModal);
  const lockMode = useDisciplineStore((s) => s.state.lockMode);
  const lockReason = useDisciplineStore((s) => s.state.lockReason);
  const lockExpiresAt = useDisciplineStore((s) => s.state.lockExpiresAt);
  const pendingUnlock = useDisciplineStore((s) => s.state.pendingUnlock);
  const settings = useDisciplineStore((s) => s.settings);
  const requestUnlock = useDisciplineStore((s) => s.requestUnlock);
  const verifyOTP = useDisciplineStore((s) => s.verifyOTP);
  const emergencyUnlock = useDisciplineStore((s) => s.emergencyUnlock);
  const canEmergencyUnlock = useDisciplineStore((s) => s.canEmergencyUnlock);
  const cancelUnlock = useDisciplineStore((s) => s.cancelUnlock);

  const addToast = useToastStore((s) => s.addToast);

  const [step, setStep] = useState<Step>('reflection');
  const [reflectionText, setReflectionText] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [otpValidIn, setOtpValidIn] = useState<number | null>(null);

  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Update timers
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      // Lock expiry countdown
      if (lockExpiresAt) {
        setTimeRemaining(Math.max(0, lockExpiresAt - Date.now()));
      }

      // OTP validity countdown
      if (pendingUnlock) {
        const validIn = pendingUnlock.otpValidAt - Date.now();
        setOtpValidIn(validIn > 0 ? validIn : 0);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isOpen, lockExpiresAt, pendingUnlock]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(pendingUnlock ? 'otp' : 'reflection');
      setReflectionText('');
      setOtpCode(['', '', '', '', '', '']);
      setError(null);
      setLoading(false);
    }
  }, [isOpen, pendingUnlock]);

  // Focus first OTP input when switching to OTP step
  useEffect(() => {
    if (step === 'otp' && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, [step]);

  if (!isOpen) return null;

  const handleReflectionSubmit = async () => {
    if (settings.reflectionRequired && reflectionText.length < settings.reflectionMinChars) {
      setError(`Please enter at least ${settings.reflectionMinChars} characters`);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await requestUnlock(reflectionText);

    setLoading(false);

    if (result.success) {
      setStep('otp');
      addToast({
        type: 'info',
        message: `Unlock code sent! Valid in ${result.validInSeconds} seconds.`,
        duration: 5000,
      });
    } else {
      setError(result.error || 'Failed to request unlock');
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);

    const newCode = [...otpCode];
    newCode[index] = digit;
    setOtpCode(newCode);

    // Auto-focus next input
    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...otpCode];
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i];
    }
    setOtpCode(newCode);

    // Focus last filled input or first empty
    const lastIndex = Math.min(pasted.length - 1, 5);
    otpInputRefs.current[lastIndex]?.focus();
  };

  const handleOtpSubmit = async () => {
    const code = otpCode.join('');
    if (code.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    if (otpValidIn && otpValidIn > 0) {
      setError(`Code not valid yet. Wait ${formatTime(otpValidIn)}`);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await verifyOTP(code);

    setLoading(false);

    if (result.success) {
      setStep('success');
      addToast({ type: 'success', message: 'Trading unlocked!' });
      setTimeout(() => closeModal(), 1500);
    } else {
      setError(result.error || 'Invalid code');
      setOtpCode(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    }
  };

  const handleEmergencyUnlock = async () => {
    if (!canEmergencyUnlock()) {
      setError('Emergency unlock is on cooldown');
      return;
    }

    const confirmed = window.confirm(
      'Emergency unlock will be logged and has a 24-hour cooldown. Are you sure?'
    );

    if (!confirmed) return;

    setLoading(true);
    const result = await emergencyUnlock();
    setLoading(false);

    if (result.success) {
      addToast({
        type: 'warning',
        message: result.warning || 'Emergency unlock used',
        duration: 5000,
      });
      closeModal();
    } else {
      setError(result.error || 'Emergency unlock failed');
    }
  };

  const handleClose = () => {
    if (pendingUnlock) {
      cancelUnlock();
    }
    closeModal();
  };

  const minChars = settings.reflectionMinChars;
  const charCount = reflectionText.length;
  const charProgress = Math.min(100, (charCount / minChars) * 100);

  return (
    <div className="rms-unlock-overlay" onClick={handleClose}>
      <div className="rms-unlock-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="rms-unlock-header">
          <div className="rms-unlock-title">
            <Lock size={18} />
            <span>Unlock Trading</span>
          </div>
          <button className="rms-unlock-close" onClick={handleClose}>
            <X size={18} />
          </button>
        </div>

        {/* Lock Info */}
        <div className="rms-unlock-info">
          <div className="rms-unlock-mode">
            <span className="label">Currently locked:</span>
            <span className="value">{LOCK_MODE_LABELS[lockMode]}</span>
          </div>
          {lockReason && (
            <div className="rms-unlock-reason">
              <span className="label">Reason:</span>
              <span className="value">{lockReason}</span>
            </div>
          )}
          {timeRemaining !== null && timeRemaining > 0 && (
            <div className="rms-unlock-timer">
              <Clock size={14} />
              <span>Auto-unlock in: {formatTime(timeRemaining)}</span>
            </div>
          )}
        </div>

        {/* Step Content */}
        <div className="rms-unlock-content">
          {step === 'reflection' && (
            <div className="rms-unlock-step reflection">
              <h3>Step 1: Why do you want to unlock?</h3>
              <p className="step-description">
                Take a moment to reflect. What's your plan? Is this trade worth it?
              </p>

              <div className="reflection-input-wrapper">
                <textarea
                  className="reflection-textarea"
                  placeholder="I see a clean setup forming..."
                  value={reflectionText}
                  onChange={(e) => setReflectionText(e.target.value)}
                  rows={4}
                />
                <div className="reflection-progress">
                  <div
                    className="reflection-progress-bar"
                    style={{ width: `${charProgress}%` }}
                  />
                </div>
                <div className="reflection-count">
                  <span className={charCount >= minChars ? 'valid' : ''}>
                    {charCount}
                  </span>
                  <span className="separator">/</span>
                  <span>{minChars} min</span>
                </div>
              </div>

              {error && <div className="rms-unlock-error">{error}</div>}

              <button
                className="rms-unlock-btn primary"
                onClick={handleReflectionSubmit}
                disabled={loading || (settings.reflectionRequired && charCount < minChars)}
              >
                {loading ? (
                  <RefreshCw size={14} className="spinning" />
                ) : (
                  <Mail size={14} />
                )}
                <span>Send Unlock Code</span>
              </button>
            </div>
          )}

          {step === 'otp' && (
            <div className="rms-unlock-step otp">
              <h3>Step 2: Enter unlock code</h3>
              <p className="step-description">
                Code sent to {settings.twoFactorEmail ? settings.twoFactorEmail.replace(/(.{2}).*@/, '$1***@') : 'your email'}
              </p>

              {otpValidIn !== null && otpValidIn > 0 && (
                <div className="otp-countdown">
                  <Clock size={14} />
                  <span>Code valid in: {formatTime(otpValidIn)}</span>
                  <div className="otp-countdown-bar">
                    <div
                      className="otp-countdown-progress"
                      style={{
                        width: `${pendingUnlock
                          ? ((pendingUnlock.otpValidAt - Date.now()) /
                              (pendingUnlock.otpValidAt - pendingUnlock.otpSentAt)) *
                            100
                          : 0}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="otp-inputs" onPaste={handleOtpPaste}>
                {otpCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className={digit ? 'filled' : ''}
                  />
                ))}
              </div>

              {error && <div className="rms-unlock-error">{error}</div>}

              <div className="otp-actions">
                <button
                  className="rms-unlock-btn secondary"
                  onClick={() => {
                    cancelUnlock();
                    setStep('reflection');
                  }}
                >
                  <span>Request New Code</span>
                </button>
                <button
                  className="rms-unlock-btn primary"
                  onClick={handleOtpSubmit}
                  disabled={loading || otpCode.join('').length !== 6 || (otpValidIn !== null && otpValidIn > 0)}
                >
                  {loading ? (
                    <RefreshCw size={14} className="spinning" />
                  ) : (
                    <CheckCircle size={14} />
                  )}
                  <span>Verify & Unlock</span>
                </button>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="rms-unlock-step success">
              <div className="success-icon">
                <CheckCircle size={48} />
              </div>
              <h3>Trading Unlocked!</h3>
              <p>Remember your plan. Trade with discipline.</p>
            </div>
          )}
        </div>

        {/* Emergency Unlock */}
        {step !== 'success' && settings.emergencyUnlockEnabled && (
          <div className="rms-unlock-emergency">
            <div className="emergency-divider">
              <span>or</span>
            </div>
            <button
              className="rms-unlock-btn emergency"
              onClick={handleEmergencyUnlock}
              disabled={loading || !canEmergencyUnlock()}
            >
              <ShieldAlert size={14} />
              <span>Emergency Unlock</span>
            </button>
            <p className="emergency-warning">
              <AlertTriangle size={12} />
              Logged for accountability. 24hr cooldown after use.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RMSUnlockModal;
